import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { siteConfig } from '@/mocks/siteConfig';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormError('');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pc222ftmtnfcfh0k90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
          || (parsed?.meta as Record<string, unknown>)?.detail as string
          || (parsed?.message as string)
          || responseText
          || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const pageTitle = '关于我们 - 洞洞杂货铺免费阅读 - 89890漫画网';
  const pageDesc = '89890漫画网品牌介绍：致力于为用户提供免费漫画阅读服务，支持洞洞杂货铺等热门漫画下拉式阅读，完整章节在线畅读，让追漫成为一种享受。了解我们的品牌故事与口碑评价。';
  const pageKeywords = '89890漫画网,关于我们,品牌介绍,洞洞杂货铺,免费阅读,下拉式漫画,口碑评价,联系方式';
  const canonicalUrl = `${siteConfig.url}/about`;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9890',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '89890漫画网的漫画都是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，89890漫画网所有漫画完全免费阅读，无需注册或付费。我们致力于为漫迷提供最优质的免费漫画阅读体验，支持下拉式阅读，滑动即可畅读完整章节。',
        },
      },
      {
        '@type': 'Question',
        name: '89890漫画网多久更新一次漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '89890漫画网的热门漫画每日更新，确保读者能够第一时间看到最新内容。我们与多家漫画平台保持合作，及时同步最新章节。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在89890漫画网上找到想看的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的分类导航浏览不同类型的漫画，也可以使用顶部的搜索功能直接搜索漫画名称。每个分类页面都提供了该类型下的所有漫画列表。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={canonicalUrl}
        schemaObjects={[organizationSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-4">
                关于<strong>89890漫画网</strong>
              </h1>
              <p className="text-sm text-foreground-500 text-center mb-12 max-w-2xl mx-auto">
                打造最好的免费漫画阅读平台，让每一个漫迷都能享受极致的阅读体验
              </p>

              <div className="prose max-w-none">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 mb-8">
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    89890漫画网成立于2024年，是国内领先的免费漫画阅读平台之一。我们的创始团队由一群热爱漫画的年轻人组成，深知漫迷对于高质量、便捷阅读体验的渴望。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    我们主打<strong>"洞洞杂货铺"</strong>等热门漫画的<strong>下拉式免费阅读</strong>体验。与传统翻页式漫画不同，下拉式漫画让读者只需滑动屏幕即可连续阅读，无需点击翻页，极大提升了阅读的流畅性和沉浸感。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    目前，89890漫画网已收录数千部热门漫画，涵盖热血、搞笑、恋爱、奇幻、悬疑、日常、科幻、武侠等多种类型，每日更新，确保读者能够第一时间看到最新内容。我们承诺所有漫画永久免费，让每一位漫迷都能享受追漫的快乐。
                  </p>
                </div>

                <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-8">
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                    用户口碑
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      {
                        name: '小漫迷',
                        text: '89890漫画网的洞洞杂货铺太好看了！下拉式阅读真的很方便，一口气看了十几话都不觉得累。',
                        rating: 5,
                      },
                      {
                        name: '追梦少年',
                        text: '分类非常清晰，找漫画很方便。最重要的是完全免费，学生党也能尽情看漫画，强烈推荐！',
                        rating: 5,
                      },
                      {
                        name: '书虫一只',
                        text: '之前用其他平台翻页阅读太麻烦了，89890的下拉模式真的是一用就回不去，追漫体验提升太多了。',
                        rating: 5,
                      },
                    ].map((review, idx) => (
                      <div key={idx} className="bg-background-50 rounded-xl p-5">
                        <div className="flex items-center gap-1 mb-3">
                          {[...Array(review.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                          "{review.text}"
                        </p>
                        <p className="text-xs text-foreground-500">— {review.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                    <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">
                      联系我们
                    </h2>
                    <div className="space-y-3 text-sm text-foreground-600">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                          <i className="ri-mail-line"></i>
                        </div>
                        <span>contact@89890.com</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                          <i className="ri-phone-line"></i>
                        </div>
                        <span>400-888-9890</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <span>北京市朝阳区漫画创意产业园</span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <h3 className="text-sm font-medium text-foreground-900 mb-4">给我们留言</h3>
                      <form
                        id="contact-form"
                        data-readdy-form
                        onSubmit={handleSubmit}
                        className="space-y-4"
                      >
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="您的称呼"
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="您的邮箱"
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            placeholder="请输入您的留言（最多500字）"
                            rows={4}
                            maxLength={500}
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                          ></textarea>
                        </div>
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="anti-spam-field"
                        />
                        <button
                          type="submit"
                          className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          发送留言
                        </button>

                        {formStatus === 'success' && (
                          <p className="text-sm text-accent-600 text-center">
                            留言发送成功！我们会尽快回复您。
                          </p>
                        )}
                        {formStatus === 'error' && (
                          <p className="text-sm text-primary-600 text-center">
                            {formError}
                          </p>
                        )}
                      </form>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-xl p-6 md:p-8">
                    <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">
                      常见问题
                    </h2>
                    <div className="space-y-4">
                      {[
                        {
                          q: '89890漫画网的漫画都是免费的吗？',
                          a: '是的，所有漫画完全免费阅读，无需注册或付费。',
                        },
                        {
                          q: '漫画多久更新一次？',
                          a: '热门漫画每日更新，确保读者第一时间看到最新内容。',
                        },
                        {
                          q: '如何找到想看的漫画？',
                          a: '通过首页分类导航或顶部搜索功能即可找到想要的漫画。',
                        },
                        {
                          q: '什么是下拉式漫画阅读？',
                          a: '下拉式阅读是一种创新的漫画浏览方式，您只需向下滑动即可连续阅读所有页面，无需逐页点击，方便快捷。',
                        },
                        {
                          q: '支持手机阅读吗？',
                          a: '完全支持！89890漫画网采用响应式设计，在手机、平板和电脑上都能获得极佳的阅读体验。',
                        },
                      ].map((faq, idx) => (
                        <details key={idx} className="group bg-background-50 rounded-lg p-4 cursor-pointer">
                          <summary className="text-sm font-medium text-foreground-900 flex items-center justify-between">
                            {faq.q}
                            <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                          </summary>
                          <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                            {faq.a}
                          </p>
                        </details>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}